import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import TrustBadge from "../components/TrustBadge";
import { toast } from "sonner";
import {
  Compass, ArrowRight, ArrowLeft, Loader2, Scale, ClipboardList, FileSignature,
  Timer, MapPin, Landmark, CheckCircle2, XCircle, ChevronDown, ChevronRight,
} from "lucide-react";

const QUESTIONS = [
  { key: "what_happened", label: "What happened?", placeholder: "Describe the situation in your own words.", type: "text", required: true },
  { key: "who_caused_it", label: "Who caused the alleged harm?", placeholder: "Person, company, agency — one line is enough.", type: "text", required: true },
  { key: "when_happened", label: "When did it happen?", placeholder: "Approximate date or period.", type: "text" },
  { key: "where_happened", label: "Where did it happen?", placeholder: "City / state / country.", type: "text" },
  { key: "contract_existed", label: "Was there a contract?", type: "choice", options: ["yes", "no", "unknown"] },
  { key: "money_lost", label: "Was money lost?", placeholder: "Amount and currency.", type: "text" },
  { key: "property_damaged", label: "Was property damaged?", placeholder: "Describe briefly.", type: "text" },
  { key: "injury", label: "Was anyone injured or harmed?", placeholder: "Describe briefly.", type: "text" },
  { key: "documents_available", label: "What documents do you have?", placeholder: "e.g. contract, invoices, emails, receipts, photos.", type: "text" },
  { key: "notice_sent", label: "Have you already sent a formal notice?", type: "choice", options: ["yes", "no"] },
  { key: "other_proceeding", label: "Has another proceeding started?", type: "choice", options: ["yes", "no"] },
  { key: "jurisdiction_hint", label: "Jurisdiction (optional)", placeholder: "e.g. India, EU-DE, US-CA", type: "text" },
];

export default function ClaimExplorerPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill || "";
  const [answers, setAnswers] = useState({ what_happened: prefill, who_caused_it: "", when_happened: "", where_happened: "", contract_existed: "unknown", money_lost: "", property_damaged: "", injury: "", documents_available: "", notice_sent: "no", other_proceeding: "no", jurisdiction_hint: "" });
  const [step, setStep] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [issueMap, setIssueMap] = useState(null);

  const setField = (k, v) => setAnswers((prev) => ({ ...prev, [k]: v }));

  const next = () => setStep((s) => Math.min(QUESTIONS.length, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const analyze = async () => {
    setAnalyzing(true);
    setIssueMap(null);
    try {
      const { data } = await api.post("/claim-explorer/analyze", answers);
      setIssueMap(data);
      toast.success("Marianne has mapped your situation.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const q = QUESTIONS[step];
  const isLast = step >= QUESTIONS.length - 1;
  const showResults = !!issueMap;

  return (
    <div data-testid="claim-explorer-page">
      <Link to="/app/cases/templates" className="text-xs text-[#4A5568] inline-flex items-center gap-1 mb-4 hover:text-[#7B56C9]">
        <ArrowLeft size={12} /> Back to Case Filing Templates
      </Link>

      <PageHeader
        eyebrow="L'Explorateur · Claim Explorer"
        title="Describe what happened. Marianne maps the rest."
        description="Answer up to twelve questions in plain language. Marianne returns a structured issue map — the claim, elements, evidence, remedies, limitation, forum — with sources and confidence attached."
      />

      {!showResults ? (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: current question */}
          <div className="lg:col-span-2 border border-[#E8E3F0] bg-white p-6 md:p-8">
            <div className="label-eyebrow text-[#C5A059] mb-3">Question {step + 1} of {QUESTIONS.length}</div>
            <h3 className="font-serif text-3xl mb-6">{q.label}</h3>

            {q.type === "text" && (
              <textarea
                autoFocus
                value={answers[q.key] || ""}
                onChange={(e) => setField(q.key, e.target.value)}
                placeholder={q.placeholder}
                rows={q.key === "what_happened" ? 5 : 3}
                className="w-full border border-[#E8E3F0] px-3 py-2 text-sm resize-y"
                data-testid={`claim-input-${q.key}`}
              />
            )}

            {q.type === "choice" && (
              <div className="flex flex-wrap gap-2">
                {q.options.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setField(q.key, opt)}
                    data-testid={`claim-choice-${q.key}-${opt}`}
                    className={`px-4 py-2 text-sm border capitalize ${answers[q.key] === opt ? "bg-[#2A0F5A] text-[#FAFAF8] border-[#2A0F5A]" : "border-[#E8E3F0] hover:border-[#7B56C9]"}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between mt-8 gap-2 flex-wrap">
              <Button variant="outline" className="rounded-none border-[#E8E3F0]" onClick={prev} disabled={step === 0} data-testid="claim-prev-btn">
                <ArrowLeft size={14} className="mr-1.5" /> Back
              </Button>
              {!isLast ? (
                <Button
                  className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]"
                  onClick={next}
                  disabled={q.required && !(answers[q.key] || "").trim()}
                  data-testid="claim-next-btn"
                >
                  Next <ArrowRight size={14} className="ml-1.5" />
                </Button>
              ) : (
                <Button
                  className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]"
                  onClick={analyze}
                  disabled={analyzing || !answers.what_happened.trim() || !answers.who_caused_it.trim()}
                  data-testid="claim-analyze-btn"
                >
                  {analyzing ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Analysing…</> : <><Compass size={14} className="mr-1.5" /> Map my issue</>}
                </Button>
              )}
            </div>
          </div>

          {/* Right: progress dots */}
          <aside className="border border-[#E8E3F0] bg-white p-5" data-testid="claim-progress">
            <div className="label-eyebrow mb-3">Progress</div>
            <ol className="space-y-1.5 text-xs">
              {QUESTIONS.map((Q, i) => {
                const done = (answers[Q.key] || "").toString().trim() && (Q.type !== "choice" || answers[Q.key]);
                const cur = i === step;
                return (
                  <li key={Q.key} onClick={() => setStep(i)} className={`cursor-pointer flex items-center gap-2 py-1 px-2 ${cur ? "bg-[#FAFAF8] font-medium" : ""}`}>
                    {done ? <CheckCircle2 size={12} className="text-[#7B56C9]" /> : <span className="w-3 h-3 rounded-full border border-[#E8E3F0] inline-block" />}
                    <span className={cur ? "text-[#2A0F5A]" : "text-[#4A5568]"}>{Q.label}</span>
                  </li>
                );
              })}
            </ol>
          </aside>
        </div>
      ) : (
        // Results
        <div className="space-y-6" data-testid="claim-issue-map">
          <TrustBadge trust={issueMap.trust} />

          <Panel title="Possible claims" icon={Scale} testid="ie-possible-claims">
            <BulletList items={issueMap.possible_claims} emptyLabel="No clear claim identified from the facts provided." />
          </Panel>

          <Panel title="Elements usually required" icon={ClipboardList} testid="ie-elements">
            <BulletList items={issueMap.elements_required} />
          </Panel>

          <div className="grid md:grid-cols-2 gap-4">
            <Panel title="Evidence you have" icon={CheckCircle2} testid="ie-evidence-available" green>
              <BulletList items={issueMap.evidence_available} emptyLabel="—" />
            </Panel>
            <Panel title="Evidence you'd still need" icon={XCircle} testid="ie-evidence-missing" amber>
              <BulletList items={issueMap.evidence_missing} emptyLabel="—" />
            </Panel>
          </div>

          <Panel title="Possible remedies" icon={FileSignature} testid="ie-remedies">
            <BulletList items={issueMap.remedies} />
          </Panel>

          <Panel title="Limitation" icon={Timer} testid="ie-limitation">
            <p className="text-sm">{issueMap.limitation || <em className="text-[#4A5568]">Not specified — jurisdiction-dependent.</em>}</p>
          </Panel>

          <Panel title="Pre-filing steps" icon={MapPin} testid="ie-prefiling">
            <BulletList items={issueMap.pre_filing} />
          </Panel>

          <Panel title="Suggested forums" icon={Landmark} testid="ie-forums">
            <BulletList items={issueMap.forums} />
          </Panel>

          <Panel title="Recommended next steps" icon={ArrowRight} testid="ie-next-steps">
            <BulletList items={issueMap.next_steps} />
          </Panel>

          <div className="flex flex-wrap gap-2">
            <Button onClick={() => { setIssueMap(null); setStep(0); }} variant="outline" className="rounded-none border-[#E8E3F0]" data-testid="claim-restart-btn">
              <ArrowLeft size={14} className="mr-1.5" /> Start over
            </Button>
            <Button onClick={() => navigate("/app/cases/new")} className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="claim-prepare-case-btn">
              <Scale size={14} className="mr-1.5" /> Prepare a case <ArrowRight size={14} className="ml-1.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Panel({ title, icon: Icon, children, testid, green, amber }) {
  const stripe = green ? "border-l-emerald-600" : amber ? "border-l-[#C5A059]" : "border-l-[#7B56C9]";
  return (
    <div className={`border border-[#E8E3F0] border-l-2 ${stripe} bg-white p-5`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} className={green ? "text-emerald-700" : amber ? "text-[#8a6b1f]" : "text-[#7B56C9]"} />
        <div className="label-eyebrow">{title}</div>
      </div>
      {children}
    </div>
  );
}

function BulletList({ items, emptyLabel = "Not specified." }) {
  if (!items || items.length === 0) return <div className="text-sm text-[#4A5568] italic">{emptyLabel}</div>;
  return (
    <ul className="text-sm space-y-1.5 list-disc pl-5">
      {items.map((i, k) => <li key={k}>{i}</li>)}
    </ul>
  );
}
